import { Icon, IconGroup, Popup, Portal, Segment } from 'semantic-ui-react'
import React, { useCallback, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Form, Header } from 'semantic-ui-react'
import { CardData, CardDataItem } from 'shared/CardData'
import { Button, generateResolver, Notification, SettingsFooter, STATUS_TYPES, VALIDATORS, yup } from 'dyl-components'
import { STATES } from 'shared/constants/STATES'
import { MathUtils, PhoneUtil, StringUtils } from 'utils'
import useWidthListener from 'shared/SettingsFooter/useWidthListener'
import useWindowWidth from 'shared/SettingsFooter/useWindowWidth'
import "./index.scss"
import tcrConfig from "actions/tcr"
import { useDispatch, useSelector } from 'react-redux'
import { alternativeBusinessTypeOptions, industryOptions } from './constants'
import FullScreenModalPopUp from 'shared/FullScreenModalPopUp'
import CustomPrompt from 'shared/confirmation/CustomPrompt'
import { useConfirm } from 'shared/confirmation/useConfirm'
import ConfirmModal from 'shared/confirmation/ConfirmModal'
import RestrictedView from 'shared/RestrictedView';
import { USER_PERMISSIONS } from 'dyl-components';

const IconLabel = ({ icon, cornerIcon, label, infoText }) => {
    const iconElement = cornerIcon ? (
        <IconGroup>
            <Icon className={icon} />
            <Icon corner className={cornerIcon} />
        </IconGroup>
    ) : (
        <Icon className={icon} />
    );
    return (
        <React.Fragment key={label}>
            {iconElement}
            <label style={{ display: 'inline-block', marginLeft: cornerIcon ? '5px' : '0px' }}>{label}</label>
            {infoText && (
                <Popup
                    trigger={
                        <Icon
                            className="fas fa-circle-info"
                            color="primary"
                            style={{ float: 'right' }}
                        />
                    }
                    position='right center'
                    content={infoText}
                    inverted
                    hoverable
                />
            )}
        </React.Fragment>
    );
}
export default function BrandRegistration() {
    const width = useWidthListener("settingsSidebar");
    const windowWidth = useWindowWidth();
    const dispatch = useDispatch();
    const [isConfirmationOpen, setIsConfirmationOpen] = useState({
        type: "",
        open: false
    });
    const { isCreatingBrand, isReadingBrand, brand, isUpdatingBrand } = useSelector((state) => state.tcr);
    const [focusedPhones, setFocusedPhones] = useState([]);
    const { control, watch, handleSubmit, formState: { isValid, isDirty }, reset, setValue } = useForm({
        defaultValues: {
            business_type: null,
            stock_symbol: null,
            stock_exchange: null,
            legal_company_name: null,
            brand_name: null,
            ein: null,
            tax_id_issuing_country: null,
            industry: null,
            brand_relationship: null,
            email: null,
            website: null,
            alternative_business_type: null,
            street: null,
            city: null,
            state: null,
            zip: null,
            primary_number: "",
        },
        mode: 'onChange',
        resolver: generateResolver({
            "business_type": yup.string().required("This field is required"),
            "stock_symbol": yup.string().when('business_type', {
                is: value => value === 'PUBLIC_PROFIT',
                then: schema => schema.required("This field is required"),
                otherwise: schema => schema.nullable(true)
            }),
            "stock_exchange": yup.string().when('business_type', {
                is: value => value === 'PUBLIC_PROFIT',
                then: schema => schema.required("This field is required"),
                otherwise: schema => schema.nullable(true)
            }),
            "legal_company_name": VALIDATORS.BUSINESS_NAME().no_whitespace_only().required("This field is required"),
            "brand_name": VALIDATORS.BUSINESS_NAME().no_whitespace_only().required("This field is required"),
            "ein": yup
                .string()
                .matches(/^\d{2}-\d{7}$/, "EIN must be in the format XX-XXXXXXX")
                .required("This field is required"),
            "tax_id_issuing_country": yup.string().maxlength(2).no_whitespace_only().simple_alphanumeric().required("This field is required"),
            "industry": yup.string().nullable(true),
            "brand_relationship": yup.string().required("This field is required"),
            "email": VALIDATORS.EMAIL_ADDRESS().maxlength(256).required("This field is required"),
            "website": VALIDATORS.WEBSITE().no_whitespace_only().maxlength(256).minlength(2).required("This field is required"),
            "alternative_business_type": yup.string().nullable(true),
            "street": yup.string().maxlength(100).required("This field is required"),
            "city": yup.string().maxlength(60).required("This field is required"),
            "state": yup.string().maxlength(2).required("This field is required"),
            "zip": VALIDATORS.US_POSTAL_CODE().simple_alphanumeric().required("This field is required"),
            "primary_number": VALIDATORS.PHONE_NUMBER().required("This field is required"),
        })
    })
    const addFocus = (type) => {
        setFocusedPhones([...focusedPhones, type]);
    };
    const removeFocus = (type) => {
        setFocusedPhones(focusedPhones.filter(phone => phone !== type));
    }
    const isFocused = (type) => {
        return focusedPhones.includes(type);
    }
    const onSubmit = async (data) => {
        setIsConfirmationOpen({
            type: "",
            open: false
        });
        const payload = {
            displayName: data.brand_name,
            companyName: data.legal_company_name,
            ein: data.ein,
            einIssuingCountry: data.tax_id_issuing_country,
            altBusinessIdType: data.alternative_business_type,
            entityType: data.business_type,
            phone: data.primary_number,
            vertical: data.industry,
            street: data.street,
            city: data.city,
            state: data.state,
            postalCode: data.zip,
            email: data.email,
            stockSymbol: data.stock_symbol || undefined,
            stockExchange: data.stock_exchange || undefined,
            website: data.website,
            brandRelationship: data.brand_relationship,
            businessContactEmail: data.email,
        }
        try {
            if (brand?.entityType && brand?.status !== "DELETED") {
                await dispatch(tcrConfig.updateBrand("", payload))
                reset(data)
            } else {
                await dispatch(tcrConfig.createBrand(payload))
                reset(data)
            }
            Notification.alert(`Successfully ${brand?.entityType ? 'updated' : 'created'}! Verification will be completed within 24 hours.`, STATUS_TYPES.SUCCESS);
            await dispatch(tcrConfig.getBrand())
        } catch (error) {
            Notification.alert(error?.Response?.length > 0 ? error?.Response[0]?.description : "Failed to register brand!", STATUS_TYPES.ERROR);
        }
    }

    const setInitialValues = useCallback(() => {
        if (brand?.entityType && brand?.status !== "DELETED") {
            reset({
                business_type: brand.entityType,
                stock_symbol: brand.stockSymbol,
                stock_exchange: brand.stockExchange,
                legal_company_name: brand.companyName,
                brand_name: brand.displayName,
                ein: brand.ein ? StringUtils.formatEINNumber(brand.ein) : null,
                tax_id_issuing_country: brand.einIssuingCountry,
                industry: brand.vertical,
                brand_relationship: brand.brandRelationship,
                email: brand.email,
                website: brand.website,
                alternative_business_type: brand.altBusinessIdType,
                street: brand.street,
                city: brand.city,
                state: brand.state,
                zip: brand.postalCode,
                primary_number: brand.phone ? PhoneUtil.removeCountryCode(brand.phone) : null,
            })
        } else {
            reset({
                business_type: null,
                stock_symbol: null,
                stock_exchange: null,
                legal_company_name: null,
                brand_name: null,
                ein: "",
                tax_id_issuing_country: null,
                industry: null,
                brand_relationship: null,
                email: null,
                website: null,
                alternative_business_type: null,
                street: null,
                city: null,
                state: null,
                zip: null,
                primary_number: "",
            })
        }
        setIsConfirmationOpen({
            type: "",
            open: false
        });
    }, [brand, reset])

    useEffect(() => {
        dispatch(tcrConfig.getBrand())
    }, [dispatch])

    useEffect(() => {
        setInitialValues()
    }, [brand, reset, setInitialValues])

    const getStatus = (status) => {
        switch (status) {
            case 'PENDING':
                return <Header as='h3'>Status:  <span className='BrandRegistrationForm__Status BrandRegistrationForm__Status--pending' >Pending</span></Header>;
            case 'ACTIVE':
                return <Header as='h3'>Status:  <span className='BrandRegistrationForm__Status BrandRegistrationForm__Status--active' >Verified</span></Header>;
            case 'REJECTED':
                return <Header as='h3'>Status:  <span className='BrandRegistrationForm__Status BrandRegistrationForm__Status--rejected' >Rejected</span></Header>;
            default:
                return <Header as='h3'>Status:  <span className='BrandRegistrationForm__Status' style={{ fontWeight: 'normal' }}>None</span></Header>
        }
    }
    const { isConfirmed } = useConfirm();

    CustomPrompt(null, isDirty, isConfirmed, 'Changes not saved', 'Are you sure you want to exit?');
    const isEditingCompanyDetailsDisabled = brand?.entityType && brand?.has_active_campaigns;
    return (
        <div className='BrandRegistrationForm'>
            <ConfirmModal />

            <Segment loading={isReadingBrand}>
                <CardData isSmallWidth>
                    <CardDataItem>
                        {getStatus(brand?.status)}
                    </CardDataItem>
                </CardData>
                {brand?.errors?.map((error, index) => {
                    return <span key={index} className='UserTableDropdown__ErrorMessage'>{error}</span>
                })}
                <Form >
                    <Header as='h2' color='primary'>
                        Brand Registration
                    </Header>
                    <Form.Group widths='equal' >
                        <Controller
                            name='business_type'
                            control={control}
                            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                                <Form.Select
                                    label={
                                        <IconLabel
                                            icon='fa-solid fa-clipboard'
                                            label='Business Type (Formally Entity Type)'
                                        />
                                    }
                                    disabled={isEditingCompanyDetailsDisabled}
                                    placeholder="Select Business Type"
                                    options={[
                                        { key: 'private_profit', value: 'PRIVATE_PROFIT', text: 'PRIVATE_PROFIT' },
                                        { key: 'PUBLIC_PROFIT', value: 'PUBLIC_PROFIT', text: 'PUBLIC_PROFIT' },
                                        { key: 'non_profit', value: 'NON_PROFIT', text: 'NON_PROFIT' },
                                        { key: 'government', value: 'GOVERNMENT', text: 'GOVERNMENT' }
                                    ]}
                                    onChange={async (_, { value }) => {
                                        if (value !== "PUBLIC_PROFIT" && watch("stock_symbol") && watch("stock_exchange")) {
                                            setValue("stock_symbol", "")
                                            setValue("stock_exchange", "")
                                        }
                                        onChange({ target: { name, value } });
                                    }}
                                    error={error?.message}
                                    value={value}
                                    required
                                />
                            )}
                        />
                        <Controller
                            name='stock_symbol'
                            control={control}
                            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                                <Form.Input
                                    name={name}
                                    value={value}
                                    disabled={watch('business_type') !== "PUBLIC_PROFIT"}
                                    onChange={(_, { value }) => { onChange({ target: { name, value } }) }}
                                    placeholder='Enter stock symbol'
                                    label='Stock Symbol'
                                    required={watch('business_type') === "PUBLIC_PROFIT"}
                                    error={error?.message}
                                />
                            )}
                        />
                        <Controller
                            name='stock_exchange'
                            control={control}
                            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                                <Form.Select
                                    name={name}
                                    value={value}
                                    disabled={watch('business_type') !== "PUBLIC_PROFIT"}
                                    onChange={(_, { value }) => { onChange({ target: { name, value } }) }}
                                    placeholder='Enter stock symbol'
                                    label='Stock Exchange'
                                    options={[
                                        { key: 'none', value: 'NONE', text: 'NONE' },
                                        { key: 'nasdaq', value: 'NASDAQ', text: 'NASDAQ' },
                                        { key: 'nyse', value: 'NYSE', text: 'NYSE' },
                                        { key: 'amex', value: 'AMEX', text: 'AMEX' },
                                        { key: 'amx', value: 'AMX', text: 'AMX' },
                                        { key: 'asx', value: 'ASX', text: 'ASX' },
                                        { key: 'b3', value: 'B3', text: 'B3' },
                                        { key: 'bme', value: 'BME', text: 'BME' },
                                        { key: 'bse', value: 'BSE', text: 'BSE' },
                                        { key: 'fra', value: 'FRA', text: 'FRA' },
                                        { key: 'icex', value: 'ICEX', text: 'ICEX' },
                                        { key: 'jpx', value: 'JPX', text: 'JPX' },
                                        { key: 'jse', value: 'JSE', text: 'JSE' },
                                        { key: 'lon', value: 'LON', text: 'LON' },
                                        { key: 'nse', value: 'NSE', text: 'NSE' },
                                        { key: 'omx', value: 'OMX', text: 'OMX' },
                                        { key: 'sehk', value: 'SEHK', text: 'SEHK' },
                                        { key: 'sgx', value: 'SGX', text: 'SGX' },
                                        { key: 'sse', value: 'SSE', text: 'SSE' },
                                        { key: 'sto', value: 'STO', text: 'STO' },
                                        { key: 'swx', value: 'SWX', text: 'SWX' },
                                        { key: 'szse', value: 'SZSE', text: 'TSSZSEX' },
                                        { key: 'tsx', value: 'TSX', text: 'TSX' },
                                        { key: 'twse', value: 'TWSE', text: 'TWSE' },
                                        { key: 'vse', value: 'VSE', text: 'VSE' },
                                        { key: 'other', value: 'OTHER', text: 'OTHER' },
                                    ]}
                                    required={watch('business_type') === "PUBLIC_PROFIT"}
                                    error={error?.message}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="legal_company_name"
                            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                                <Form.Input
                                    name={name}
                                    value={value}
                                    onChange={(_, { value }) => { onChange({ target: { name, value } }) }}
                                    placeholder='Type Legal Company Name'
                                    label={
                                        <IconLabel
                                            icon='fa-solid fa-database'
                                            label='Legal Company Name'
                                        />
                                    }
                                    disabled={isEditingCompanyDetailsDisabled}
                                    error={error?.message}
                                    required
                                />
                            )}
                        />

                    </Form.Group>
                    <Form.Group widths='equal'>
                        <Controller
                            control={control}
                            name="brand_name"
                            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                                <RestrictedView type="readOnlyInput" userPermission={USER_PERMISSIONS.BRAND_REGISTRATION_EDIT}>
                                    <Form.Input
                                        name={name}
                                        value={value}
                                        onChange={(_, { value }) => { onChange({ target: { name, value } }) }}
                                        placeholder='Enter brand name'
                                        label={
                                            <IconLabel
                                                icon='fas fa-building'
                                                cornerIcon='fas fa-user'
                                                label='Brand Name (DBA)'
                                            />
                                        }
                                        error={error?.message}
                                        required
                                    />
                                </RestrictedView>
                            )}
                        />
                        <Controller
                            control={control}
                            name="ein"
                            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                                <Form.Input
                                    name={name}
                                    value={value}
                                    onChange={(_, { value }) => { onChange({ target: { name, value } }) }}
                                    placeholder='Enter EIN'
                                    disabled={isEditingCompanyDetailsDisabled}
                                    error={error?.message}
                                    label={
                                        <IconLabel
                                            icon='fas fa-building'
                                            cornerIcon='fas fa-user'
                                            label='EIN'
                                        />
                                    }
                                    required
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="tax_id_issuing_country"
                            render={({ field, fieldState: { error } }) => (
                                <Form.Input
                                    placeholder='Select tax ID country'
                                    label='Tax ID Issuing Country'
                                    disabled={isEditingCompanyDetailsDisabled}
                                    {...field}
                                    error={error?.message}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="industry"
                            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                                <RestrictedView type="readOnlyDropdown" userPermission={USER_PERMISSIONS.BRAND_REGISTRATION_EDIT}>
                                    <Form.Select
                                        name={name}
                                        value={value}
                                        onChange={(_, { value }) => { onChange({ target: { name, value } }) }}
                                        placeholder='Select industry'
                                        required
                                        label={
                                            <IconLabel
                                                icon='fa-solid fa-industry'
                                                label='Industry'
                                            />
                                        }
                                        options={industryOptions}
                                        error={error?.message}
                                    />
                                </RestrictedView>
                            )}
                        />

                    </Form.Group>
                    <Form.Group widths='four'>
                        <Controller
                            control={control}
                            name="brand_relationship"
                            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                                <RestrictedView type="readOnlyDropdown" userPermission={USER_PERMISSIONS.BRAND_REGISTRATION_EDIT}>
                                    <Form.Select
                                        name={name}
                                        value={value}
                                        onChange={(_, { value }) => { onChange({ target: { name, value } }) }}
                                        placeholder='Select brand relationship'
                                        label='Brand Relationship'
                                        options={[
                                            { key: 'basic_account', value: 'BASIC_ACCOUNT', text: 'Basic Account' },
                                            { key: 'small_account', value: 'SMALL_ACCOUNT', text: 'Small Account' },
                                            { key: 'medium_account', value: 'MEDIUM_ACCOUNT', text: 'Medium Account' },
                                            { key: 'large_account', value: 'LARGE_ACCOUNT', text: 'Large Account' },
                                            { key: 'key_account', value: 'KEY_ACCOUNT', text: 'Key Account' }
                                        ]}
                                        required
                                        error={error?.message}
                                    />
                                </RestrictedView>
                            )}
                        />
                        <Controller
                            control={control}
                            name="email"
                            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                                <RestrictedView type="readOnlyInput" userPermission={USER_PERMISSIONS.BRAND_REGISTRATION_EDIT}>
                                    <Form.Input
                                        name={name}
                                        value={value}
                                        type="email"
                                        onChange={(_, { value }) => { onChange({ target: { name, value } }) }}
                                        placeholder='Type Support Email'
                                        label={
                                            <IconLabel
                                                icon='fas fa-envelope'
                                                label='Support Email'
                                            />
                                        }
                                        required
                                        error={error?.message}
                                    />
                                    </RestrictedView>
                            )}
                        />
                        <Controller
                            name='website'
                            control={control}
                            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                                <RestrictedView type="readOnlyInput" userPermission={USER_PERMISSIONS.BRAND_REGISTRATION_EDIT}>
                                    <Form.Input
                                        onChange={(_, { name, value }) => { onChange({ target: { name, value } }) }}
                                        value={value}
                                        error={error?.message}
                                        name={name}
                                        placeholder='Type Website'
                                        required
                                        label={
                                            <IconLabel
                                                icon='fas fa-globe'
                                                label='Website'
                                                infoText={`Provide the URL for your brand's official website or social media profile`}
                                            />
                                        }
                                    />
                                </RestrictedView>
                            )}
                        />
                        <Controller
                            control={control}
                            name="alternative_business_type"
                            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                                <RestrictedView type="readOnlyDropdown" userPermission={USER_PERMISSIONS.BRAND_REGISTRATION_EDIT}>
                                    <Form.Select
                                        name={name}
                                        value={value}
                                        onChange={(_, { value }) => { onChange({ target: { name, value } }) }}
                                        placeholder='Select Alternative Business Identifier Type'
                                        label="Alternative Business Identifier Type"
                                        options={alternativeBusinessTypeOptions}
                                        error={error?.message}
                                    />
                                </RestrictedView>
                            )}
                        />
                    </Form.Group>

                    <Header as='h2' color='primary'>
                        Business Info
                    </Header>

                    <Form.Group widths='3'>
                        <Controller
                            name="street"
                            control={control}
                            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                                <RestrictedView type="readOnlyInput" userPermission={USER_PERMISSIONS.BRAND_REGISTRATION_EDIT}>
                                    <Form.Input
                                        onChange={(_, { name, value }) => { onChange({ target: { name, value } }) }}
                                        value={value}
                                        error={error?.message}
                                        name={name}
                                        placeholder='Street Address'
                                        label={
                                            <IconLabel
                                                icon='fas fa-location-dot'
                                                label='Street Address'
                                            />
                                        }
                                        required
                                    />
                                </RestrictedView>
                            )}
                        />
                        <Form.Group widths='equal'>
                            <Controller
                                name="city"
                                control={control}
                                render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                                    <RestrictedView type="readOnlyInput" userPermission={USER_PERMISSIONS.BRAND_REGISTRATION_EDIT}>
                                        <Form.Input
                                            onChange={(_, { name, value }) => { onChange({ target: { name, value } }) }}
                                            value={value}
                                            error={error?.message}
                                            name={name}
                                            placeholder='City'
                                            label={"City"}
                                            required
                                        />
                                    </RestrictedView>
                                )}
                            />
                            <Controller
                                name="state"
                                control={control}
                                render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                                    <RestrictedView type="readOnlyDropdown" userPermission={USER_PERMISSIONS.BRAND_REGISTRATION_EDIT}>
                                        <Form.Select
                                            name={name}
                                            value={value}
                                            onChange={(_, { value }) => {
                                                onChange({ target: { name, value } })
                                            }}
                                            placeholder='State'
                                            search
                                            label={"State"}
                                            required
                                            options={STATES.map(state => ({ ...state, value: state.key }))}
                                            error={error?.message}
                                        />
                                    </RestrictedView>
                                )}
                            />
                            <Controller
                                name="zip"
                                control={control}
                                render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                                    <RestrictedView type="readOnlyInput" userPermission={USER_PERMISSIONS.BRAND_REGISTRATION_EDIT}>
                                        <Form.Input
                                            onChange={(_, { name, value }) => { onChange({ target: { name, value } }) }}
                                            value={value}
                                            error={error?.message}
                                            name={name}
                                            placeholder='Zip'
                                            label={"Zip"}
                                            required
                                        />
                                    </RestrictedView>
                                )}
                            />
                        </Form.Group>
                    </Form.Group>
                    <Form.Group widths='four'>
                        <Controller
                            name='primary_number'
                            control={control}
                            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                                <RestrictedView type="readOnlyInput" userPermission={USER_PERMISSIONS.BRAND_REGISTRATION_EDIT}>
                                    <Form.Input
                                        onChange={(_, { name, value }) => { onChange({ target: { name, value } }) }}
                                        name={name}
                                        value={!isFocused('primary_number') ? PhoneUtil.formatPhoneNumber(value) : value}
                                        error={error?.message}
                                        onFocus={() => { addFocus('primary_number') }}
                                        onBlur={() => { removeFocus('primary_number') }}
                                        label={<IconLabel icon='text telephone' label='Primary Phone Number' />}
                                        required
                                    />
                                </RestrictedView>
                            )}
                        />
                    </Form.Group>
                </Form>
                <Portal open>
                    <SettingsFooter
                        style={{ width: MathUtils.calculatePercentage(windowWidth, windowWidth - width) }}
                        rightOptions={[
                            <Button onClick={() => {
                                setIsConfirmationOpen({
                                    type: "cancel",
                                    open: true
                                });
                            }} disabled={isCreatingBrand || isUpdatingBrand || !isDirty} status={'cancel'} basic>Cancel</Button>,
                            <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.BRAND_REGISTRATION_ADD}>
                                <Button loading={isCreatingBrand || isUpdatingBrand} disabled={isCreatingBrand || isUpdatingBrand || !isDirty || !isValid} onClick={() => {
                                    setIsConfirmationOpen({
                                        type: "submit",
                                        open: true
                                    });
                                }} primary>Submit</Button> 
                            </RestrictedView>
                        ]}
                    />
                </Portal>
                <FullScreenModalPopUp
                    header={isConfirmationOpen.type === "submit" ? "Are you sure?" : "Changes not saved"}
                    subheader={
                        isConfirmationOpen.type === "submit" ? "$4 will be charged to your account." : "Are you sure you want to exit?"
                    }
                    isOpen={isConfirmationOpen.open}
                    onConfirm={isConfirmationOpen.type === "submit" ? handleSubmit(onSubmit) : setInitialValues}
                    onFormClose={() => setIsConfirmationOpen({
                        type: "",
                        open: false
                    })}
                    closeIcon={false}
                />
            </Segment>
        </div>
    )
}
